<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType, INBOX_TYPES } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  showStats: {
    type: Boolean,
    default: false,
  },
  showAnalytics: {
    type: Boolean,
    default: false,
  },
  template: {
    type: [Object, String],
    default: null,
  },
});

const emit = defineEmits(['edit', 'delete', 'stats', 'analytics']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';
const STATUS_FAILED = 'failed';
const ACTIVE_STATUSES = ['scheduled', STATUS_PROCESSING];

const { formatMessage } = useMessageFormatter();

function templatePreview(tpl) {
  if (!tpl) return '';
  try {
    const parsed = typeof tpl === 'string' ? JSON.parse(tpl) : tpl;
    const parts =
      parsed?.components
        ?.filter(c => ['HEADER', 'BODY'].includes(c.type) && c.text)
        ?.map(c => c.text) || [];
    const text = parts.join(' ').trim();
    return text.length > 80 ? `${text.slice(0, 80)}…` : text;
  } catch {
    return '';
  }
}

const displayMessage = computed(() => {
  if (props.message) return props.message;
  return templatePreview(props.template);
});

const isActive = computed(() =>
  props.isLiveChatType
    ? props.isEnabled
    : ACTIVE_STATUSES.includes(props.status)
);

const statusTextColor = computed(() => ({
  'text-n-ruby-11': props.status === STATUS_FAILED,
  'text-n-teal-11': isActive.value && props.status !== STATUS_FAILED,
  'text-n-slate-12': !isActive.value && props.status !== STATUS_FAILED,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.showStats) {
    if (props.status === STATUS_COMPLETED) {
      return t('CAMPAIGN.CSV.WHATSAPP.CARD.STATUS.COMPLETED');
    }
    if (props.status === STATUS_PROCESSING) {
      return t('CAMPAIGN.CSV.WHATSAPP.CARD.STATUS.SENDING');
    }
    if (props.status === STATUS_FAILED) {
      return t('CAMPAIGN.CSV.WHATSAPP.CARD.STATUS.FAILED');
    }

    return t('CAMPAIGN.CSV.WHATSAPP.CARD.STATUS.SCHEDULED');
  }

  if (props.inbox?.channel_type === INBOX_TYPES.WHATSAPP) {
    if (props.status === STATUS_COMPLETED) {
      return t('CAMPAIGN.WHATSAPP.CARD.STATUS.COMPLETED');
    }
    if (props.status === STATUS_PROCESSING) {
      return t('CAMPAIGN.WHATSAPP.CARD.STATUS.PROCESSING');
    }
    if (props.status === STATUS_FAILED) {
      return t('CAMPAIGN.WHATSAPP.CARD.STATUS.FAILED');
    }

    return t('CAMPAIGN.WHATSAPP.CARD.STATUS.SCHEDULED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }
  if (props.status === STATUS_PROCESSING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(displayMessage, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end w-20 gap-2">
      <Button
        v-if="showAnalytics"
        v-tooltip.top="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-chart-no-axes-column"
        :title="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        @click="emit('analytics')"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        v-if="showStats"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-bar-chart-2"
        :title="t('CAMPAIGN.CSV.WHATSAPP.STATS.VIEW_STATS')"
        @click="emit('stats')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
